// src/pages/rh/DetailContrat.jsx
import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Spinner, Alert, 
    Modal, Table, Form, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import Footer from "../../layouts/Footer";
import Header from "../../layouts/Header";
import { formatDate } from "../../utils/formatters";
import { API_BASE_URL } from "../../constants/api";
import { API_BASE } from "../../constants/apibase";
import logo from "../../assets/img/IMG-20250130-WA0008.jpg";

export default function DetailContrat() {
    const navigate = useNavigate();
    const { id } = useParams();
    
    const [contrat, setContrat] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');

    useEffect(() => {
        fetchContrat();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setSocieteInfos(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => {
                    console.warn("Impossible de charger le logo");
                    setLogoBase64('');
                    resolve('');
                };
                img.src = logo;
            } catch (error) {
                console.error("Erreur chargement logo:", error);
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchContrat = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setContrat(result.data);
            setEditForm(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (field, fallbackFileName) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?action=download&id=${id}&field=${field}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const error = await response.json();
                alert(error.message || 'Erreur');
                return;
            }

            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = fallbackFileName;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) fileName = match[1];
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert('Erreur de téléchargement');
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSuccessMessage("Contrat supprimé avec succès !");
                setShowSuccessAlert(true);
                setTimeout(() => {
                    navigate('/rh/contrats');
                }, 2000);
            } else {
                setError(result.message);
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError("Erreur lors de la suppression");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = async () => {
        setEditing(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/contrats.php?id=${id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editForm)
            });

            const result = await response.json();

            if (result.success) {
                setShowEditModal(false);
                fetchContrat();
                setSuccessMessage("Contrat modifié avec succès !");
                setShowSuccessAlert(true);
                setTimeout(() => setShowSuccessAlert(false), 3000);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError("Erreur lors de la modification");
        } finally {
            setEditing(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const getStatutBadge = () => {
        if (!contrat) return { variant: 'secondary', text: 'Inconnu', icon: 'ri-question-line' };
        
        const today = new Date();
        const dateDebut = new Date(contrat.date_debut);
        const dateFin = contrat.date_fin ? new Date(contrat.date_fin) : null;
        
        if (contrat.statut === 'termine') {
            return { variant: 'secondary', text: 'Terminé', icon: 'ri-checkbox-circle-line' };
        }
        if (dateFin && dateFin < today) {
            return { variant: 'danger', text: 'Expiré', icon: 'ri-error-warning-line' };
        }
        if (dateDebut > today) {
            return { variant: 'warning', text: 'À venir', icon: 'ri-time-line' };
        }
        return { variant: 'success', text: 'Actif', icon: 'ri-checkbox-circle-line' };
    };

    const getJoursRestants = () => {
        if (!contrat || !contrat.date_fin) return null;
        const dateFin = new Date(contrat.date_fin);
        const today = new Date();
        const joursRestants = Math.ceil((dateFin - today) / (1000 * 60 * 60 * 24));
        return joursRestants;
    };

    // Informations société
    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com'
    };

    // Génération HTML pour impression professionnelle
    const genererHTMLImpression = () => {
        if (!contrat) return '';

        const logoSrc = logoBase64;
        const statut = getStatutBadge();
        const joursRestants = getJoursRestants();
        
        // Calcul de la durée du contrat
        const dateDebut = new Date(contrat.date_debut);
        const dateFin = contrat.date_fin ? new Date(contrat.date_fin) : null;
        let dureeContrat = '';
        if (dateFin) {
            const diffTime = Math.abs(dateFin - dateDebut);
            const diffJours = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            const mois = Math.floor(diffJours / 30);
            const jours = diffJours % 30;
            dureeContrat = mois > 0 ? `${mois} mois ${jours} jours` : `${diffJours} jours`;
        } else {
            dureeContrat = 'Contrat à durée indéterminée (CDI)';
        }

      return `
    <!DOCTYPE html>
    <html lang="fr">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Contrat de travail - ${contrat.employe_prenom} ${contrat.employe_nom}</title>
        <style>
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            
            body {
                font-family: 'Arial', 'Helvetica', sans-serif;
                background: white;
                padding: 0;
                margin: 0;
                color: #2c3e50;
                font-size: 10pt;
                line-height: 1.4;
            }
            
            .contrat-container {
                max-width: 210mm;
                margin: 0 auto;
                background: white;
            }
            
            /* En-tête */
            .header {
                padding: 20px 25px 15px 25px;
                border-bottom: 2px solid #2c3e50;
                margin-bottom: 20px;
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            
            .logo-area {
                flex: 1;
                text-align: left;
            }
            
            .logo-img {
                height: 60px;
                width: auto;
            }
            
            .title-area {
                flex: 2;
                text-align: center;
            }
            
            .document-title {
                font-size: 16pt;
                font-weight: bold;
                text-transform: uppercase;
                color: #2c3e50;
                letter-spacing: 1px;
            }
            
            .document-subtitle {
                font-size: 9pt;
                color: #7f8c8d;
                margin-top: 3px;
            }
            
            .number-area {
                flex: 1;
                text-align: right;
            }
            
            .document-number {
                font-size: 10pt;
                font-weight: bold;
                color: #2c3e50;
                background: #ecf0f1;
                padding: 4px 10px;
                border-radius: 4px;
                display: inline-block;
            }
            
            /* Corps */
            .body {
                padding: 0 25px;
            }
            
            /* Coordonnées société */
            .company-contact {
                text-align: center;
                font-size: 8pt;
                color: #7f8c8d;
                margin-bottom: 20px;
                padding-bottom: 10px;
                border-bottom: 1px solid #ecf0f1;
            }
            
            /* Sections */
            .section {
                margin-bottom: 20px;
            }
            
            .section-title {
                font-size: 11pt;
                font-weight: bold;
                text-transform: uppercase;
                color: #2c3e50;
                border-left: 3px solid #2c3e50;
                padding-left: 10px;
                margin-bottom: 12px;
            }
            
            /* Grille d'informations */
            .info-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 15px;
                margin-bottom: 15px;
            }
            
            .info-card {
                border: 1px solid #ecf0f1;
                padding: 12px;
                background: #fafbfc;
            }
            
            .info-card-header {
                font-weight: bold;
                margin-bottom: 10px;
                padding-bottom: 6px;
                border-bottom: 1px solid #ecf0f1;
                font-size: 10pt;
                color: #2c3e50;
            }
            
            .info-row {
                margin-bottom: 6px;
                display: flex;
                font-size: 9pt;
            }
            
            .info-label {
                width: 70px;
                font-weight: bold;
                color: #7f8c8d;
                flex-shrink: 0;
            }
            
            .info-value {
                color: #2c3e50;
                flex: 1;
            }
            
            /* Tableau des détails */
            .details-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 10px;
            }
            
            .details-table tr {
                border-bottom: 1px solid #ecf0f1;
            }
            
            .details-table td {
                padding: 8px 0;
            }
            
            .details-table td:first-child {
                width: 30%;
                font-weight: bold;
                color: #7f8c8d;
            }
            
            .details-table td:last-child {
                color: #2c3e50;
            }
            
            /* Badge statut */
            .status-badge {
                display: inline-block;
                padding: 3px 12px;
                font-size: 9pt;
                font-weight: bold;
                border-radius: 3px;
            }
            
            .status-actif { background: #27ae60; color: white; }
            .status-expire { background: #e74c3c; color: white; }
            .status-avenir { background: #f39c12; color: white; }
            .status-termine { background: #95a5a6; color: white; }
            
            /* Signatures */
            .signature-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 40px;
                margin-top: 15px;
                margin-bottom: 15px;
            }
            
            .signature-line {
                border-top: 1px solid #2c3e50;
                margin-top: 25px;
                margin-bottom: 5px;
                width: 100%;
            }
            
            .signature-label {
                font-size: 8pt;
                color: #7f8c8d;
                margin-top: 3px;
            }
            
            .signature-date {
                font-size: 8pt;
                color: #7f8c8d;
                margin-top: 5px;
            }
            
            /* Mentions légales */
            .legal-mentions {
                font-size: 7pt;
                text-align: center;
                color: #95a5a6;
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #ecf0f1;
            }
            
            /* Pied de page */
            .footer {
                padding: 10px 25px;
                border-top: 1px solid #ecf0f1;
                font-size: 7pt;
                text-align: center;
                color: #95a5a6;
                margin-top: 15px;
            }
            
            @media print {
                body {
                    padding: 0;
                    margin: 0;
                }
                .status-badge {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .info-card {
                    background: none;
                }
            }
        </style>
    </head>
    <body>
        <div class="contrat-container">
            <!-- En-tête aligné : logo gauche, titre centre, numéro droite -->
            <div class="header">
                <div class="logo-area">
                    ${logoSrc ? `<img src="${logoSrc}" alt="Logo" class="logo-img" />` : `<strong style="font-size: 14pt;">${company.name}</strong>`}
                </div>
                <div class="title-area">
                    <div class="document-title">CONTRAT DE TRAVAIL</div>
                    <div class="document-subtitle">Document officiel</div>
                </div>
                <div class="number-area">
                    <div class="document-number">N° CT-${contrat.id}</div>
                </div>
            </div>
            
            <div class="body">
                <!-- Coordonnées société -->
                <div class="company-contact">
                    ${company.address} | Tél: ${company.phone} | Email: ${company.email}
                </div>
                
                <!-- Informations générales -->
                <div class="section">
                    <div class="section-title">INFORMATIONS GENERALES</div>
                    <div class="info-grid">
                        <div class="info-card">
                            <div class="info-card-header">EMPLOYEUR</div>
                            <div class="info-row">
                                <div class="info-label">Société</div>
                                <div class="info-value"><strong>${contrat.employeur_nom}</strong></div>
                            </div>
                            ${contrat.employeur_adresse ? `
                            <div class="info-row">
                                <div class="info-label">Adresse</div>
                                <div class="info-value">${contrat.employeur_adresse}</div>
                            </div>
                            ` : ''}
                            ${contrat.employeur_email ? `
                            <div class="info-row">
                                <div class="info-label">Email</div>
                                <div class="info-value">${contrat.employeur_email}</div>
                            </div>
                            ` : ''}
                            ${contrat.employeur_telephone ? `
                            <div class="info-row">
                                <div class="info-label">Téléphone</div>
                                <div class="info-value">${contrat.employeur_telephone}</div>
                            </div>
                            ` : ''}
                        </div>
                        <div class="info-card">
                            <div class="info-card-header">EMPLOYE</div>
                            <div class="info-row">
                                <div class="info-label">Nom complet</div>
                                <div class="info-value"><strong>${contrat.employe_prenom} ${contrat.employe_nom}</strong></div>
                            </div>
                            ${contrat.employe_adresse ? `
                            <div class="info-row">
                                <div class="info-label">Adresse</div>
                                <div class="info-value">${contrat.employe_adresse}</div>
                            </div>
                            ` : ''}
                            ${contrat.employe_email ? `
                            <div class="info-row">
                                <div class="info-label">Email</div>
                                <div class="info-value">${contrat.employe_email}</div>
                            </div>
                            ` : ''}
                            ${contrat.employe_telephone ? `
                            <div class="info-row">
                                <div class="info-label">Téléphone</div>
                                <div class="info-value">${contrat.employe_telephone}</div>
                            </div>
                            ` : ''}
                        </div>
                    </div>
                </div>
                
                <!-- Détails du contrat -->
                <div class="section">
                    <div class="section-title">DETAILS DU CONTRAT</div>
                    <table class="details-table">
                        <tr>
                            <td>Type de contrat</td>
                            <td><strong>${contrat.date_fin ? 'Contrat à Durée Déterminée (CDD)' : 'Contrat à Durée Indéterminée (CDI)'}</strong></td>
                        </tr>
                        <tr>
                            <td>Date de début</td>
                            <td><strong>${formatDate(contrat.date_debut)}</strong></td>
                        </tr>
                        ${contrat.date_fin ? `
                        <tr>
                            <td>Date de fin</td>
                            <td><strong>${formatDate(contrat.date_fin)}</strong></td>
                        </tr>
                        ` : ''}
                        <tr>
                            <td>Statut</td>
                            <td>
                                <span class="status-badge status-${statut.variant === 'success' ? 'actif' : (statut.variant === 'danger' ? 'expire' : (statut.variant === 'warning' ? 'avenir' : 'termine'))}">
                                    ${statut.text}
                                </span>
                            </td>
                        </tr>
                        ${joursRestants !== null && joursRestants > 0 ? `
                        <tr>
                            <td>Jours restants</td>
                            <td><strong>${joursRestants} jour(s) avant expiration</strong></td>
                        </tr>
                        ` : ''}
                    </table>
                </div>
                
                <!-- Documents associés -->
                ${(contrat.lettre_fin_contrat || contrat.certificat_travail) ? `
                <div class="section">
                    <div class="section-title">DOCUMENTS ASSOCIES</div>
                    <div style="font-size: 9pt; padding: 5px 0;">
                        ${contrat.lettre_fin_contrat ? `<div>• Lettre de fin de contrat : ${contrat.lettre_fin_contrat.split('/').pop()}</div>` : ''}
                        ${contrat.certificat_travail ? `<div>• Certificat de travail : ${contrat.certificat_travail.split('/').pop()}</div>` : ''}
                    </div>
                </div>
                ` : ''}
                
                <!-- Signatures -->
                <div class="section">
                    <div class="section-title">SIGNATURES</div>
                    <div class="signature-grid">
                        <div>
                            <div class="signature-line"></div>
                            <div class="signature-label">Signature de l'employeur</div>
                            <div class="signature-date">Fait à ${contrat.fait_a || '_______________'}, le ${formatDate(new Date())}</div>
                        </div>
                        <div>
                            <div class="signature-line"></div>
                            <div class="signature-label">Signature de l'employé</div>
                            <div class="signature-date">Lu et approuvé, le ${formatDate(new Date())}</div>
                        </div>
                    </div>
                </div>
                
                <!-- Mentions légales -->
                <div class="legal-mentions">
                    Conformément aux articles L. 1221-1 et suivants du Code du travail, le présent contrat fait foi entre les parties.
                </div>
            </div>
            
            <!-- Pied de page -->
            <div class="footer">
                Document généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
            </div>
        </div>
    </body>
    </html>
`;
    };

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        
        setSuccessMessage("Document envoyé à l'impression");
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false, loading = false }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button 
                variant={variant} 
                onClick={onClick} 
                disabled={disabled || loading} 
                className="action-btn"
                style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '10px', 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    color: '#64748b'
                }}
            >
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    const joursRestants = getJoursRestants();
    const statut = getStatutBadge();

    if (loading && !contrat) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du contrat...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !contrat) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Contrat non trouvé"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/rh/contrats')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour à la liste
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail du contrat</h4>
                                    <p className="text-muted small mb-0">
                                        Contrat de {contrat.employe_prenom} {contrat.employe_nom}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <ActionButton variant="outline-secondary" icon="ri-arrow-left-line" onClick={() => navigate('/rh/contrats')} tooltip="Retour" />
                            <ActionButton variant="outline-primary" icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer le contrat" />
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => setShowEditModal(true)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                style={{ color: '#21ACCD', borderColor: '#21ACCD' }}
                            >
                                <i className="ri-edit-line"></i>
                                Modifier
                            </Button>
                            <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => setShowDeleteModal(true)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                            >
                                <i className="ri-delete-bin-line"></i>
                                Supprimer
                            </Button>
                        </div>
                    </div>

                    {/* Carte de statut */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-file-copy-line text-primary fs-4" style={{ color: '#21ACCD' }}></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Contrat</small>
                                            <h4 className="mb-0">#{contrat.id}</h4>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                            <i className="ri-calendar-line text-success fs-4"></i>
                                        </div>
                                        <div>
                                            <small className="text-muted">Période</small>
                                            <h5 className="mb-0">
                                                {formatDate(contrat.date_debut)}
                                                {contrat.date_fin && ` → ${formatDate(contrat.date_fin)}`}
                                                {!contrat.date_fin && ' → CDI'}
                                            </h5>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-end">
                                        <span className="status-badge" style={{
                                            background: statut.variant === 'success' ? '#10b981' : 
                                                       statut.variant === 'warning' ? '#f59e0b' : 
                                                       statut.variant === 'danger' ? '#ef4444' : '#6c757d',
                                            color: 'white', padding: '0.5rem 1rem', borderRadius: '30px', fontSize: '0.75rem', fontWeight: '600'
                                        }}>
                                            <i className={`${statut.icon} me-1`}></i>
                                            {statut.text}
                                        </span>
                                        {joursRestants !== null && joursRestants > 0 && (
                                            <div className="mt-2">
                                                <Badge bg="warning" className="rounded-pill" style={{color: 'white'}}>
                                                    <i className="ri-time-line me-1"></i>
                                                    {joursRestants} jour(s) restants
                                                </Badge>
                                            </div>
                                        )}
                                        {joursRestants !== null && joursRestants <= 0 && joursRestants !== null && (
                                            <div className="mt-2">
                                                <Badge bg="danger" className="rounded-pill">
                                                    <i className="ri-error-warning-line me-1"></i>
                                                    Contrat expiré
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Informations employeur */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-building-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Informations employeur
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="info-row mb-3">
                                        <div className="info-label">Nom de l'entreprise</div>
                                        <div className="info-value fw-semibold">{contrat.employeur_nom}</div>
                                    </div>
                                    {contrat.employeur_adresse && (
                                        <div className="info-row mb-3">
                                            <div className="info-label">Adresse</div>
                                            <div className="info-value">{contrat.employeur_adresse}</div>
                                        </div>
                                    )}
                                    {contrat.employeur_email && (
                                        <div className="info-row mb-3">
                                            <div className="info-label">Email</div>
                                            <div className="info-value">
                                                <a href={`mailto:${contrat.employeur_email}`}>{contrat.employeur_email}</a>
                                            </div>
                                        </div>
                                    )}
                                    {contrat.employeur_telephone && (
                                        <div className="info-row">
                                            <div className="info-label">Téléphone</div>
                                            <div className="info-value">
                                                <a href={`tel:${contrat.employeur_telephone}`}>{contrat.employeur_telephone}</a>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Informations employé */}
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-user-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Informations employé
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="info-row mb-3">
                                        <div className="info-label">Nom complet</div>
                                        <div className="info-value fw-semibold">{contrat.employe_prenom} {contrat.employe_nom}</div>
                                    </div>
                                    {contrat.employe_adresse && (
                                        <div className="info-row mb-3">
                                            <div className="info-label">Adresse</div>
                                            <div className="info-value">{contrat.employe_adresse}</div>
                                        </div>
                                    )}
                                    {contrat.employe_email && (
                                        <div className="info-row mb-3">
                                            <div className="info-label">Email</div>
                                            <div className="info-value">
                                                <a href={`mailto:${contrat.employe_email}`}>{contrat.employe_email}</a>
                                            </div>
                                        </div>
                                    )}
                                    {contrat.employe_telephone && (
                                        <div className="info-row">
                                            <div className="info-label">Téléphone</div>
                                            <div className="info-value">
                                                <a href={`tel:${contrat.employe_telephone}`}>{contrat.employe_telephone}</a>
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Détails du contrat */}
                    <Row className="g-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-calendar-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Période du contrat
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table borderless className="mb-0">
                                        <tbody>
                                            <tr>
                                                <td style={{ width: '40%' }} className="text-muted">Date de début</td>
                                                <td className="fw-semibold">{formatDate(contrat.date_debut)}</td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Date de fin</td>
                                                <td className="fw-semibold">
                                                    {contrat.date_fin ? formatDate(contrat.date_fin) : 'CDI (Contrat à durée indéterminée)'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="text-muted">Type de contrat</td>
                                                <td>
                                                    <Badge bg={contrat.date_fin ? 'info' : 'primary'} className="rounded-pill">
                                                        {contrat.date_fin ? 'CDD' : 'CDI'}
                                                    </Badge>
                                                </td>
                                             </tr>
                                            <tr>
                                                <td className="text-muted">Statut</td>
                                                <td>
                                                    <Badge bg={statut.variant} className="rounded-pill">
                                                        <i className={`${statut.icon} me-1`}></i>
                                                        {statut.text}
                                                    </Badge>
                                                </td>
                                             </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Documents */}
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-file-list-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Documents
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">Lettre de fin de contrat</span>
                                            {contrat.lettre_fin_contrat ? (
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm"
                                                    className="rounded-pill"
                                                    onClick={() => handleDownload('lettre_fin_contrat', contrat.lettre_fin_contrat.split('/').pop())}
                                                >
                                                    <i className="ri-download-line me-1"></i> Télécharger
                                                </Button>
                                            ) : (
                                                <Badge bg="secondary" className="rounded-pill">
                                                    <i className="ri-close-line me-1"></i> Non remis
                                                </Badge>
                                            )}
                                        </div>
                                        {contrat.lettre_fin_contrat && (
                                            <div className="small text-muted">
                                                <i className="ri-file-pdf-line me-1"></i> {contrat.lettre_fin_contrat.split('/').pop()}
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="text-muted">Certificat de travail</span>
                                            {contrat.certificat_travail ? (
                                                <Button 
                                                    variant="outline-success" 
                                                    size="sm"
                                                    className="rounded-pill"
                                                    onClick={() => handleDownload('certificat_travail', contrat.certificat_travail.split('/').pop())}
                                                >
                                                    <i className="ri-download-line me-1"></i> Télécharger
                                                </Button>
                                            ) : (
                                                <Badge bg="secondary" className="rounded-pill">
                                                    <i className="ri-close-line me-1"></i> Non remis
                                                </Badge>
                                            )}
                                        </div>
                                        {contrat.certificat_travail && (
                                            <div className="small text-muted">
                                                <i className="ri-file-pdf-line me-1"></i> {contrat.certificat_travail.split('/').pop()}
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    <div className="metadata-section mt-4 text-center text-muted small">
                        <i className="ri-information-line me-1"></i>
                        ID: #{contrat.id} • Créé le {formatDate(contrat.created_at)}
                    </div>
                </div>
                <Footer />
            </div>

            {/* Modal de suppression */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                            </div>
                            <h3 className="fw-bold text-white">Confirmer la suppression</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-4 text-center">
                    <p className="text-muted mb-3">Êtes-vous sûr de vouloir supprimer ce contrat ?</p>
                    <div className="bg-light p-3 rounded-3 mb-3">
                        <strong className="text-primary d-block">{contrat.employe_prenom} {contrat.employe_nom}</strong>
                        <small className="text-muted">{contrat.employeur_nom}</small>
                        <div className="mt-1">
                            Du {formatDate(contrat.date_debut)} au {contrat.date_fin ? formatDate(contrat.date_fin) : 'CDI'}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} className="rounded-pill px-4">
                        Annuler
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting} className="rounded-pill px-5">
                        {deleting ? <><Spinner size="sm" className="me-2" />Suppression...</> : 'Confirmer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de modification */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ background: '#21ACCD', color: 'white' }}>
                    <Modal.Title>
                        <i className="ri-edit-line me-2"></i>
                        Modifier le contrat
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                            <i className="ri-building-line me-2"></i>
                            Employeur
                        </h6>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Nom de l'entreprise</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="employeur_nom"
                                        value={editForm.employeur_nom || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Adresse</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="employeur_adresse"
                                        value={editForm.employeur_adresse || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="employeur_email"
                                        value={editForm.employeur_email || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Téléphone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="employeur_telephone"
                                        value={editForm.employeur_telephone || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                            <i className="ri-user-line me-2"></i>
                            Employé
                        </h6>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Nom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="employe_nom"
                                        value={editForm.employe_nom || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Prénom</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="employe_prenom"
                                        value={editForm.employe_prenom || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="employe_email"
                                        value={editForm.employe_email || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Téléphone</Form.Label>
                                    <Form.Control
                                        type="tel"
                                        name="employe_telephone"
                                        value={editForm.employe_telephone || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Adresse</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="employe_adresse"
                                        value={editForm.employe_adresse || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                            <i className="ri-calendar-line me-2"></i>
                            Période
                        </h6>
                        <Row className="g-3 mb-4">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Date de début</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date_debut"
                                        value={editForm.date_debut || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Date de fin (laisser vide pour CDI)</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="date_fin"
                                        value={editForm.date_fin || ''}
                                        onChange={handleEditChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <h6 className="mb-3 fw-semibold" style={{ color: '#21ACCD' }}>
                            <i className="ri-settings-line me-2"></i>
                            Statut
                        </h6>
                        <Row className="g-3 mb-4">
                            <Col md={12}>
                                <Form.Select
                                    name="statut"
                                    value={editForm.statut || 'actif'}
                                    onChange={handleEditChange}
                                >
                                    <option value="actif">Actif</option>
                                    <option value="termine">Terminé</option>
                                    <option value="expire">Expiré</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowEditModal(false)} className="rounded-pill">
                        Annuler
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleEdit} 
                        disabled={editing}
                        className="rounded-pill"
                        style={{ background: '#21ACCD', border: 'none' }}
                    >
                        {editing ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : 'Enregistrer'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .info-row { margin-bottom: 1rem; }
                .info-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 0.25rem; }
                .info-value { font-size: 0.95rem; }
                .status-badge { display: inline-flex; align-items: center; gap: 0.5rem; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
                .rounded-3 { border-radius: 12px !important; }
                .rounded-pill { border-radius: 50px !important; }
                .fs-20 { font-size: 1.25rem; }
                .alert-modern { border: none; border-radius: 12px; padding: 1rem 1.5rem; box-shadow: 0 4px 8px rgba(0,0,0,0.05); }
                .modern-modal .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 40px rgba(0,0,0,0.2); overflow: hidden; }
                .confirm-icon { animation: scaleIn 0.5s ease-out; }
                .action-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
                .btn-outline-primary:hover { background: linear-gradient(135deg, #21ACCD, #1a8ba8); border-color: #21ACCD; color: white; }
                .btn-outline-secondary:hover { background: linear-gradient(135deg, #64748b, #475569); border-color: #64748b; color: white; }
                .btn-outline-danger:hover { background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; color: white; }
                @keyframes scaleIn { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
        </>
    );
}